import { supabase } from '@/integrations/supabase/client';
import { subDays, subMonths } from 'date-fns';

export type Chat = {
  id: string;
  user_id: string;
  title: string | null;
  created_at: string;
  updated_at: string;
  context_data: any;
  organization_id: string | null;
  folder_id: string | null;
  is_pinned: boolean;
  pinned_at: string | null;
  chat_mode: ChatMode;
};

export interface SearchFilters {
  query?: string;
  folderId?: string | null;
  dateRange?: { from: Date; to: Date } | null;
  datePreset?: 'any' | 'week' | 'month' | '3months' | 'custom';
  folders?: string[];
  searchInContent?: boolean;
  pinnedOnly?: boolean;
}

export type Message = {
  id: string;
  chat_id: string;
  role: 'user' | 'assistant';
  content: string;
  created_at: string;
  metadata?: {
    sources?: Array<{
      title: string;
      url?: string;
      score?: number;
    }>;
    chunks?: Array<{
      text: string;
      score?: number;
    }>;
    files?: Array<{
      name: string;
      size: number;
      type: string;
      url: string;
      extractedText: string;
    }>;
  } | null;
};

export type ChatMode =
  | 'funcionarios'
  | 'oposiciones'
  | 'oposiciones_c1'
  | 'oposiciones_guardia_civil'
  | 'oposiciones_policia_nacional'
  | 'oposiciones_iipp'
  | 'oposiciones_c2'
  | 'oposiciones_generico';

export async function createChat(userId: string, chatMode: ChatMode = 'funcionarios') {
  const { data: chat, error: chatError } = await supabase
    .from('chats')
    .insert({
      user_id: userId,
      title: 'Nueva consulta',
      chat_mode: chatMode,
    } as any)
    .select()
    .single();

  if (chatError) throw chatError;

  return chat;
}

export async function listChatsByUser(userId: string, chatMode?: ChatMode) {
  let query = supabase
    .from('chats')
    .select('*')
    .eq('user_id', userId) as any;

  if (chatMode) {
    query = query.eq('chat_mode', chatMode);
  }

  const { data, error } = await query.order('updated_at', { ascending: false });

  if (error) throw error;
  return data as Chat[];
}

export async function getChatById(chatId: string) {
  const { data, error } = await supabase
    .from('chats')
    .select('*')
    .eq('id', chatId)
    .single();

  if (error) throw error;
  return data as Chat;
}

export async function updateChatTitle(chatId: string, title: string) {
  const { error } = await supabase
    .from('chats')
    .update({ title })
    .eq('id', chatId);

  if (error) throw error;
}

export async function deleteChat(chatId: string) {
  const { error } = await supabase
    .from('chats')
    .delete()
    .eq('id', chatId);

  if (error) throw error;
}

export async function getChatMessages(
  chatId: string,
  limit: number = 20,
  before?: string
): Promise<Message[]> {
  let query = supabase
    .from('messages')
    .select('*')
    .eq('chat_id', chatId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (before) {
    query = query.lt('created_at', before);
  }

  const { data, error } = await query;

  if (error) throw error;
  return (data as Message[]).reverse(); // Reverse to show oldest first
}

export async function sendMessage(
  chatId: string, 
  content: string, 
  role: 'user' | 'assistant' = 'user',
  metadata?: any
) {
  const { data, error } = await supabase
    .from('messages')
    .insert({
      chat_id: chatId,
      role,
      content,
      metadata: metadata || null,
    })
    .select()
    .single();

  if (error) throw error;
  return data as Message;
}

// Placeholder for AI integration (will be implemented in Module #5)
export async function sendToAI(chatId: string, userMessage: string): Promise<string> {
  // Simulate AI processing delay
  await new Promise(resolve => setTimeout(resolve, 1500));
  
  // Placeholder response
  return "Esta es una respuesta simulada del asistente jurídico. La integración con AnythingLLM se implementará en el Módulo #5. Por ahora, este es un placeholder que demuestra la funcionalidad del chat.";
}

export async function searchChats(userId: string, query: string, chatMode?: ChatMode) {
  let dbQuery = supabase
    .from('chats')
    .select('*')
    .eq('user_id', userId);
  
  if (chatMode) {
    dbQuery = dbQuery.eq('chat_mode', chatMode);
  }

  const { data, error } = await dbQuery
    .or(`title.ilike.%${query}%`)
    .order('updated_at', { ascending: false });

  if (error) throw error;
  return data as Chat[];
}

// Toggle pin status for a chat
export async function togglePinChat(chatId: string, isPinned: boolean) {
  const { error } = await supabase
    .from('chats')
    .update({ 
      is_pinned: isPinned,
      pinned_at: isPinned ? new Date().toISOString() : null
    })
    .eq('id', chatId);

  if (error) throw error;
}

// Get pinned chats for a user
export async function getPinnedChats(userId: string): Promise<Chat[]> {
  const { data, error } = await supabase
    .from('chats')
    .select('*')
    .eq('user_id', userId)
    .eq('is_pinned', true)
    .order('pinned_at', { ascending: false });

  if (error) throw error;
  return data as Chat[];
}

// Advanced search with filters
export async function advancedSearchChats(
  userId: string,
  filters: SearchFilters,
  chatMode?: ChatMode
): Promise<Chat[]> {
  // Calculate date range based on preset
  let dateRange = filters.dateRange;
  if (filters.datePreset && filters.datePreset !== 'any' && filters.datePreset !== 'custom') {
    const now = new Date();
    switch (filters.datePreset) {
      case 'week':
        dateRange = { from: subDays(now, 7), to: now };
        break;
      case 'month':
        dateRange = { from: subMonths(now, 1), to: now };
        break;
      case '3months':
        dateRange = { from: subMonths(now, 3), to: now };
        break;
    }
  }

  // If searching in content, we need to use a direct query with join
  if (filters.searchInContent && filters.query) {
    // Use a direct query with join for content search
    let query = supabase
      .from('chats')
      .select(`
        *,
        messages!inner(content)
      `)
      .eq('user_id', userId);

    if (chatMode) {
      query = query.eq('chat_mode', chatMode);
    }

    // Apply text filter on both title and content
    query = query.or(`title.ilike.%${filters.query}%,messages.content.ilike.%${filters.query}%`);

    // Apply pinned filter
    if (filters.pinnedOnly) {
      query = query.eq('is_pinned', true);
    }

    // Apply date range filter
    if (dateRange) {
      query = query
        .gte('updated_at', dateRange.from.toISOString())
        .lte('updated_at', dateRange.to.toISOString());
    }

    // Apply folder filter
    if (filters.folders && filters.folders.length > 0) {
      const hasUncategorized = filters.folders.includes('uncategorized');
      const folderIds = filters.folders.filter(id => id !== 'uncategorized');
      
      if (hasUncategorized && folderIds.length > 0) {
        query = query.or(`folder_id.is.null,folder_id.in.(${folderIds.join(',')})`);
      } else if (hasUncategorized) {
        query = query.is('folder_id', null);
      } else {
        query = query.in('folder_id', folderIds);
      }
    }

    query = query.order('updated_at', { ascending: false });

    const { data, error } = await query;

    if (error) {
      console.error('Content search error:', error);
      // Fallback to basic search
      return searchChats(userId, filters.query, chatMode);
    }
    
    // Remove duplicates and return
    const uniqueChats = Array.from(new Map(data.map((c: any) => [c.id, c])).values());
    return uniqueChats as Chat[];
  }

  // Basic query without content search
  let query = supabase
    .from('chats')
    .select('*')
    .eq('user_id', userId);

  if (chatMode) {
    query = query.eq('chat_mode', chatMode);
  }

  // Apply text filter
  if (filters.query) {
    query = query.ilike('title', `%${filters.query}%`);
  }

  // Apply pinned filter
  if (filters.pinnedOnly) {
    query = query.eq('is_pinned', true);
  }

  // Apply date range filter
  if (dateRange) {
    query = query
      .gte('updated_at', dateRange.from.toISOString())
      .lte('updated_at', dateRange.to.toISOString());
  }

  // Apply folder filter
  if (filters.folders && filters.folders.length > 0) {
    const hasUncategorized = filters.folders.includes('uncategorized');
    const folderIds = filters.folders.filter(id => id !== 'uncategorized');
    
    if (hasUncategorized && folderIds.length > 0) {
      query = query.or(`folder_id.is.null,folder_id.in.(${folderIds.join(',')})`);
    } else if (hasUncategorized) {
      query = query.is('folder_id', null);
    } else {
      query = query.in('folder_id', folderIds);
    }
  }

  query = query.order('updated_at', { ascending: false });

  const { data, error } = await query;

  if (error) throw error;
  return data as Chat[];
}
